import type { Plugin } from "vue";
import { createApp } from "vue";
import i18n from "@/i18n";
import type { ILfVuexStore } from "./models/state";
import { createPinia } from "pinia";

import App from "@/App.vue";
import globalComponents from "./globalComponents";
import ClickOutsidePlugin from "@/plugins/ClickOutsidePlugin";
import VueGtag from "vue-gtag";
import Chartkick from "vue-chartkick";
import { vMaska } from "maska";
import mitt from "mitt";
import router from "@/router";
import store from "@/store";
import "@/assets/global.css";
import PrimeVue from "primevue/config";
import "primevue/resources/primevue.min.css";
import "primevue/resources/themes/saga-blue/theme.css";

// eslint-disable-next-line import/extensions
import "@fontsource/inter/latin.css";
import "chartkick/chart.js";
import "@/assets/tailwind.css";
import { establishWebSocketConnection } from "./helpers/websockets";
import config from "./config";
import "primeicons/primeicons.css";
import { plugin as VueTippy } from "vue-tippy";
import "tippy.js/dist/tippy.css";
import "@/assets/quill.css";

import * as Sentry from "@sentry/vue";

const app = createApp(App)
  .use(router)
  .use(i18n)
  .use(store as ILfVuexStore & Plugin)
  .use(createPinia())
  // eslint-disable-next-line
  // @ts-ignore
  .use(Chartkick)
  .use(ClickOutsidePlugin)
  .use(PrimeVue)
  .use(VueTippy, {
    component: "tooltip",
    directive: "tooltip",
    defaultProps: { theme: "lendflow" }
  });

Object.entries(globalComponents).forEach(([name, component]) =>
  app.component(name, component)
);

app.config.globalProperties.emitter = mitt();

if (config.env === "production" && config.google.googleTagId) {
  if (config.google.googleTagId) {
    app.use(
      VueGtag,
      {
        config: {
          id: config.google.googleTagId
        },
        appName: "dashboard",
        deferScriptLoad: true
      },
      router
    );
  }
}

establishWebSocketConnection();
Sentry.init({
  app,
  dsn: config.sentryDashboardDsn,
  release: config.sentryRelease,
  dist: config.sentryDist,
  integrations: [
    new Sentry.BrowserTracing({
      routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      tracingOrigins: [config.api.baseUrl || "localhost", /^\//]
    })
  ],
  sampleRate: 1,
  // disable trace sampling
  tracesSampleRate: 0,
  maxBreadcrumbs: 50
});

app.directive("maska", vMaska);

app.mount("#app");
